<script setup lang="ts">
import type { UIMessage } from "ai";
import ThinkingBlock from "./ThinkingBlock.vue";
import ToolCallBlock from "./ToolCallBlock.vue";
import ToolApprovalCard from "./ToolApprovalCard.vue";
import MarkdownRenderer from "./MarkdownRenderer.vue";
import GenerativeBlock from "./generative/GenerativeBlock.vue";
import ChatThinkingIndicator from "./ChatThinkingIndicator.vue";
import ChatSources from "./ChatSources.vue";
import ResponseRatingPrompt from "./ResponseRatingPrompt.vue";
import { computed, inject } from "vue";
import { getAssistantRenderParts } from "../utils/assistantRenderParts";
import { getMessageSources } from "../utils/docSources";
import { ApprovalResponderKey } from "../composables/approvalResponder";
import { ResponseRatingKey } from "../composables/responseRating";

const props = defineProps<{
  message: UIMessage;
  isStreaming: boolean;
}>();

const renderParts = computed(() => getAssistantRenderParts(props.message));

// The backend writes the data-sources part once, after the last text delta, so this list is empty
// until the message finalizes — no extra streaming guard needed. (RTLCA-4937)
const sources = computed(() => getMessageSources(props.message));

const respondToApproval = inject(ApprovalResponderKey);

// Null unless this message is one of the replies picked for a rating prompt (1st, 5th, 10th, ...) (RTLCA-4748).
// Optional injection so the component still renders in Storybook and wherever no panel provides it.
const responseRating = inject(ResponseRatingKey, null);
const ratingPrompt = computed(() => responseRating?.promptFor(props.message.id) ?? null);
</script>

<template>
  <div class="gap-px-8 flex flex-col">
    <span class="chat-text-muted t-footnote">AI Agent</span>

    <template
      v-for="part in renderParts"
      :key="part.key">
      <ThinkingBlock
        v-if="part.type === 'reasoning'"
        class="max-w-[85%]"
        :thinking="part.reasoning"
        :is-streaming="isStreaming" />

      <template v-else-if="part.type === 'tool'">
        <GenerativeBlock
          v-if="part.toolName.startsWith('render_')"
          class="max-w-[85%]"
          :tool-name="part.toolName"
          :result="part.result"
          :is-loading="part.isLoading" />
        <ToolCallBlock
          v-else
          class="max-w-[85%]"
          :tool-name="part.toolName"
          :tool-input="part.input as Record<string, unknown>"
          :result="part.result"
          :is-error="part.isError"
          :is-loading="part.isLoading" />
      </template>

      <ToolApprovalCard
        v-else-if="part.type === 'approval'"
        class="max-w-[85%]"
        :tool-name="part.toolName"
        :input="part.input as Record<string, unknown>"
        :approval-id="part.approvalId"
        @approve="(id) => respondToApproval?.(id, true)"
        @deny="(id) => respondToApproval?.(id, false)" />

      <div
        v-else
        class="chat-assistant-bubble rounded-8 px-px-16 py-px-12 max-w-[85%] border">
        <MarkdownRenderer :content="part.text" />
      </div>
    </template>

    <ChatSources :sources="sources" />

    <ResponseRatingPrompt
      v-if="ratingPrompt && !isStreaming"
      :state="ratingPrompt"
      @rate="(rating, comment) => responseRating?.submit(message.id, rating, comment)"
      @dismiss="responseRating?.dismiss(message.id)" />

    <ChatThinkingIndicator v-if="isStreaming && renderParts.length === 0" />
  </div>
</template>
